import { useQuery } from '@tanstack/react-query';
import { getRebookingAvailability, type RebookingAvailability } from './api';

/**
 * Pre-run policy gate (rebooking TDS §18 "opt-in by absence"): does the
 * rebooking engine run at all for this station/leg/cabin? A policy read —
 * it changes only when the policy catalog changes — so it is cached long and
 * shared by every PNR on the same key.
 *
 * While loading, and on any read error, the result is `undefined`: consumers
 * must treat that as "no gate" and keep today's behavior — an action is
 * withheld only on a definitive `enabled: false`, never on a blip.
 */
export function useRebookingAvailability(params: {
  airport?: string | undefined;
  leg?: string | undefined;
  cabin?: string | undefined;
  /** Extra gate for the query itself (e.g. only once the drawer is open). */
  enabled?: boolean;
}): RebookingAvailability | undefined {
  const { airport, leg, cabin } = params;
  const query = useQuery({
    queryKey: ['rebooking', 'availability', airport ?? null, leg ?? null, cabin ?? null],
    queryFn: () => getRebookingAvailability({ airport, leg, cabin }),
    enabled: (params.enabled ?? true) && !!airport,
    staleTime: 5 * 60_000,
  });
  return query.data;
}
